<i18n src="@/locales/models/harvested_batch.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/harvested_package.yml" lang="yaml"></i18n>
<i18n
  src="@/locales/views/packing_house/process/manage_harvested_batch/edit_harvested_batch.yml"
  lang="yaml"
></i18n>

<template>
  <div>
    <go-back-link
      :text="$t('labels.go_back_link')"
      name="ph_process_harvested_batches_show"
      :params="{
        packing_house_process_id: packing_house_process_id,
        harvested_batch_id: harvested_batch_id
      }"
    />
    <bread-crumbs
      :labels="[
        module_name,
        packing_house_process.name,
        $t('labels.harvested_batches_title')
      ]"
    ></bread-crumbs>

    <alert-danger :errors_data="std_err" />

    <div class="mt-8">
      <div class="panel" v-if="harvested_batch">
        <div class="panel-header">
          <h3 class="panel-header-title">
            {{ $t("labels.title") }}
          </h3>
        </div>

        <div class="panel-body">
          <div
            v-if="harvested_batch"
            class="space-y-8 divide-y divide-gray-200"
          >
            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.batch_code") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.trim="harvested_batch.batch_code"
                    :class="['input-text', isInvalid('batch_code')]"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.registered_at") }}
                </label>
                <div class="mt-1">
                  <date-form v-model="harvested_batch.registered_at" />
                </div>
              </div>
            </div>

            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 pt-6">
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.producer") }}
                </label>
                <div class="mt-1">
                  <v-select
                    v-if="producers_organizations?.length > 1"
                    name="producer_or_organization"
                    class="custom-tailwind-select"
                    v-model="producer_organization_selected"
                    :options="producers_organizations"
                  />
                </div>
              </div>

              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.crop_field") }}
                </label>
                <div class="mt-1">
                  <v-select
                    v-if="crop_field_options.length > 0"
                    name="crop_field"
                    class="custom-tailwind-select"
                    v-model="crop_field_selected"
                    :options="crop_field_options"
                  />
                </div>
              </div>
            </div>

            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 pt-6">
              <template
                v-for="(crop_har_package, idx) in crop_har_packages"
                :key="crop_har_package.id"
              >
                <div class="sm:col-span-3">
                  <label class="label-form">{{
                    $t("model.harvested_package.crop_har_package")
                  }}</label>
                  <div class="mt-1">
                    <div class="content-data">
                      {{ crop_har_package.name }}
                    </div>
                  </div>
                </div>
                <div class="sm:col-span-3">
                  <label class="label-form">
                    {{ $t("model.harvested_package.number") }}
                  </label>
                  <div class="flex mt-1">
                    <input
                      type="text"
                      v-model.number.trim="harvested_packages[idx].number"
                      :class="['input-text', isInvalid('number')]"
                    />
                  </div>
                </div>
              </template>
            </div>

            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 pt-6">
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.total_weight") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.number.trim="harvested_batch.total_weight"
                    :class="['input-text', isInvalid('total_weight')]"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.discarded_weight") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.number.trim="harvested_batch.discarded_weight"
                    :class="['input-text', isInvalid('discarded_weight')]"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.ok_weight") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.number.trim="harvested_batch.ok_weight"
                    :class="['input-text', isInvalid('ok_weight')]"
                  />
                </div>
              </div>
            </div>
            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 pt-6">
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.crop_type") }}
                </label>
                <div class="mt-1">
                  <select-form
                    v-if="crop_types.length > 0"
                    :options="crop_types"
                    v-model="harvested_batch.crop_type_id"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.crop_variety") }}
                </label>
                <div class="mt-1">
                  <select-form
                    v-if="crop_varieties.length > 0"
                    :options="crop_varieties"
                    v-model="harvested_batch.crop_variety_id"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.agro_exporter") }}
                </label>
                <div class="mt-1">
                  <select-form
                    v-if="agro_exporters.length > 0"
                    :options="agro_exporters"
                    v-model="harvested_batch.agro_exporter_id"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="panel-footer">
          <div class="panel-footer-actions">
            <white-button @click.prevent="goToShowHarvestedBatch()" :label='$t("labels.cancel_button")' />
            <span class="space-actions"></span>
            <success-button v-if="!charging_submit" @click.prevent="update()" :label="$t('labels.save_button')" />
            <success-button-processing v-else :label="$t('labels.save_button')" />
          </div>
        </div>
      </div>
      <loading-message v-else />
    </div>
  </div>
</template>

<script>
import RequesterQuery from "@/views/packing_house/requesters/process/harvested_batches/query.js";
import Requester from "@/views/packing_house/requesters/process/harvested_batches/manage_harvested_batch.js";
import BreadCrumbs from "@/components/views/layout/BreadCrumbs.vue";

export default {
  name: "EditHarvestedBatch",
  components: { BreadCrumbs },
  data() {
    return {
      charging_submit: false,
      packing_house_process: {},
      harvested_batch: null,
      producer_organization_selected: null,
      crop_field_selected: null,
      crop_types: [],
      crop_varieties: [],
      agro_exporters: [],
      producers_organizations: [],
      crop_har_packages: [],
      harvested_packages: []
    };
  },
  props: {
    packing_house_process_id: {
      required: true
    },
    harvested_batch_id: {
      required: true
    },
    packing_house_id: {
      required: true
    }
  },
  created() {
    this.checkIfAuthenticated().then(() => {
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          packing_house_id: this.packing_house_id
        }
      }).then(() => {
        this.fetchPackingHouseProcess();
        this.fetchProducersOrganizations().then(() =>
          this.fetchHarvestedBatch().then(() => {
            this.fetchCropHarPackages();
          })
        );
        this.fetchCropTypes();
        this.fetchCropVarieties();
        this.fetchAgroExporters();
      });
    });
  },
  computed: {
    crop_field_options: function () {
      if (!this.producer_organization_selected) return [];

      return this.producer_organization_selected.crop_fields;
    },
    harvested_packages_params: function () {
      return this.harvested_packages.filter(
        harvested_package =>
          Boolean(harvested_package.id) || Number(harvested_package.number) > 0
      );
    },
    validates: function () {
      return (
        this.harvested_packages.filter(
          harvested_package => Number(harvested_package.number) > 0
        ).length > 0
      );
    }
  },
  watch: {
    crop_field_options: function (opts) {
      let size = opts.length;
      if (size == 0) this.crop_field_selected = null;
      if (size == 1) this.crop_field_selected = opts[0];
    },
  },
  methods: {
    fetchPackingHouseProcess: async function () {
      const response = await Requester.fetchPackingHouseProcess(
        this.packing_house_process_id
      );
      this.packing_house_process = response.data;
    },
    fetchCropTypes: async function () {
      const response = await RequesterQuery.fetchCropTypes(
        this.packing_house_process_id
      );
      this.crop_types = response.data;
    },
    fetchCropVarieties: async function () {
      const response = await RequesterQuery.fetchCropVarieties(
        this.packing_house_process_id
      );
      this.crop_varieties = response.data;
    },
    fetchAgroExporters: async function () {
      const response = await RequesterQuery.fetchAgroExporters(
        this.packing_house_process_id
      );
      this.agro_exporters = response.data;
    },
    fetchProducersOrganizations: async function () {
      const response = await RequesterQuery.fetchProducersOrganizations(
        this.packing_house_process_id
      );
      this.producers_organizations = response.data;
    },
    fetchCropHarPackages: async function () {
      const response = await RequesterQuery.fetchCropHarPackages(
        this.packing_house_process_id
      );
      this.crop_har_packages = response.data;
      this.harvested_packages = this.crop_har_packages.map(crop_har_package => {
        return {
          id: this.harvested_batch.harvested_packages.find(
            harvested_package =>
              harvested_package.crop_har_package_id === crop_har_package.id
          )?.id,
          crop_har_package_id: crop_har_package.id,
          number: this.harvested_batch.harvested_packages.find(
            harvested_package =>
              harvested_package.crop_har_package_id === crop_har_package.id
          )?.number
        };
      });
    },
    fetchHarvestedBatch: async function () {
      const response = await Requester.fetchHarvestedBatch(
        this.harvested_batch_id
      );
      this.harvested_batch = response.data;
      this.producer_organization_selected = this.producers_organizations.find(
        item =>
          item.producer_id == this.harvested_batch.producer_id &&
          item.organization_id == this.harvested_batch.organization_id
      );
    },
    update: async function () {
      if (!this.validates) return;

      this.charging_submit = true;
      this.harvested_batch.producer_id =
        this.producer_organization_selected?.producer_id;
      this.harvested_batch.organization_id =
        this.producer_organization_selected?.organization_id;
      try {
        let response = await Requester.update(this.harvested_batch_id, {
          batch_code: this.harvested_batch.batch_code,
          total_weight: this.harvested_batch.total_weight,
          discarded_weight: this.harvested_batch.discarded_weight,
          ok_weight: this.harvested_batch.ok_weight,
          registered_at: this.harvested_batch.registered_at,
          crop_type_id: this.harvested_batch.crop_type_id,
          crop_variety_id: this.harvested_batch.crop_variety_id,
          producer_id: this.harvested_batch.producer_id,
          organization_id: this.harvested_batch.organization_id,
          crop_field_id: this.crop_field_selected?.id,
          agro_exporter_id: this.harvested_batch.agro_exporter_id,
          harvested_packages: this.harvested_packages_params
        });

        this.registerSuccessNotification({
          title: response.data.success_message
        });
        this.goToShowHarvestedBatch();
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields
          });
        }
      }
      this.charging_submit = false;
    },
    cropHarPackageLabel: function (crop_har_package_id) {
      return this.crop_har_packages.find(
        crop_har_package => crop_har_package.id == crop_har_package_id
      )?.name;
    },
    goToShowHarvestedBatch: function () {
      this.$router.push({
        name: "ph_process_harvested_batches_show",
        params: {
          packing_house_process_id: this.packing_house_process_id
        }
      });
    }
  }
};
</script>
